import React, { FunctionComponent, ReactElement, useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { useMedia } from 'react-media-match';
import { Image } from '@empiriecom/module-components/Image';
import { Modal } from '@empiriecom/module-components/Modal';
import { Esi } from '@empiriecom/module-components/Esi';
import FormattedCurrency from '@empiriecom/module-components/FormattedCurrency';
import useConfig from '@empiriecom/module-components/hooks/useConfig';
import useFetch from '@empiriecom/module-components/hooks/useFetch';
import useTracking from '@empiriecom/module-components/hooks/useTracking';
import getIcon from '@empiriecom/module-components/utils/getIcon';
import { TippBadge } from '@/src/components/utils';
import { FragmentConfiguration } from '@/config';
import { useBasket } from '@/src/components/ProductContext';
import { AttributeBlock, AttributeValueObject, Attribute } from '@/src/interfaces';
import { Services, Cluster, Service } from './types';
import Card from '../Card';
import useUrlParameters from '../hooks/useUrlParameters';

const ServicesWrapper = styled.div<{ mobile: boolean }>`
  ${({ mobile }) => !mobile && 'border-top: 1px solid #f4f4f4;'};
  ${({ mobile }) => !mobile && 'margin: 1rem 0;'};
  user-select: none;
  @media only screen and (max-width: 1024px) {
    margin-right: 1rem;
  }
`;

const ServicesHeadline = styled.div<{ mobile: boolean }>`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 0.5rem 1.25rem;
  ${({ mobile }) => !mobile && 'border-bottom: 1px solid #f4f4f4;'};
  ${({ mobile }) => !mobile && 'margin-bottom: 1.25rem;'};
  > span {
    margin-left: 0.5rem;
    font-weight: bold;
    font-size: 1rem;
    display: block;
  }
`;

const ClusterWrapper = styled.div<{ mobile: boolean }>`
  ${({ mobile }) => !mobile && 'margin: 0 1rem;'};
  padding: 0.5rem 0 0.6875rem;
  border-bottom: 1px solid #f4f4f4;
  &&:last-of-type {
    border-bottom: 0px none;
    padding-bottom: 0;
  }
`;

const ClusterLabel = styled.div<{ mobile: boolean }>`
  font-size: 0.6875rem;
  line-height: 1.5rem;
  ${({ mobile }) => !mobile && 'padding: 0 1rem;'};
  font-weight: bold;
  text-transform: uppercase;
`;

const ServiceWrapper = styled.div<{ mobile: boolean }>`
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  border-bottom: 1px solid #f4f4f4;
  margin-bottom: 0.5rem;
  padding: ${({ mobile }) => (mobile ? '0 0 0.5rem;' : '0 1rem 0.5rem;')};
  &&:last-of-type {
    border-bottom: 0px none;
    margin-bottom: 0;
  }
`;

const ServiceCheck = styled.div`
  width: 1.375rem;
  margin-right: 0.75rem;
`;

const ServiceCheckboxWrapper = styled.div`
  margin-top: 0.25rem;
  position: relative;
  width: 1rem;
  height: 1rem;
  border-radius: 3px;
  background-image: linear-gradient(to bottom, #ffffff, #f5f5f5);
  border: 1px solid #959a9c;
`;

const ServicePrice = styled.div<{ inclusive: boolean }>`
  ${({ inclusive }) => (inclusive ? 'color: #7dae2e;' : 'cursor: pointer;')};
  font-weight: bold;
  text-align: right;
  flex-grow: 1;
  font-size: 0.8125rem;
  white-space: nowrap;
  label {
    cursor: pointer;
  }
`;

const ServiceName = styled.div`
  width: 12.25rem;
  margin-right: 0.75rem;
  font-size: 0.8125rem;
  display: flex;
  flex-direction: column;
  label {
    cursor: pointer;
  }
`;

const ServiceCheckbox = styled.input`
  display: block;
  visibility: hidden;
  position: absolute;
  left: -9999rem;
  top: auto;
  &&:checked + label {
    opacity: 1;
  }
`;

const ServiceCheckboxLabel = styled.label`
  opacity: 0;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ServiceIcon = styled.label<{ inclusive: boolean }>`
  width: 1.5rem;
  height: 1.5rem;
  margin-right: 0.5rem;
  ${({ inclusive }) => (inclusive ? 'cursor: default;' : 'cursor: pointer;')};
`;

const PremiumServiceIcon = styled.div`
  width: 1.5rem;
  height: 1.5rem;
  margin-right: 0.5rem;
`;

const ModalLink = styled.div`
  text-decoration: underline;
  color: #a0a0a0;
  font-size: 0.6875rem;
  cursor: pointer;
  &&:hover {
    color: #a0a0a0;
    border: 0px none;
  }
`;

const ModalEsiContainer = styled.div`
  .product-services-grey {
    background-color: #efeeee;
    padding: 1rem;
  }
  .product-services-image-headline {
    display: flex;
    justify-content: flex-start;
    align-items: center;
  }
  .product-services-image {
    width: 3.5rem;
    img {
      width: 100%;
      height: auto;
    }
  }
  .product-services-headline {
    font-size: 1.5rem;
    margin-left: 1rem;
  }
  .product-services-bulletpoint-headline {
    margin-top: 1rem;
    font-weight: bold;
  }
  .product-services-bulletpoints {
    border: 1px solid #dcdcdc;
    border-radius: 3px;
    margin-top: 0.5rem;
    padding: 1rem;
  }
  .product-services-bulletpoint {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    :before {
      content: '•';
      color: ${({ theme: { primary } }) => primary.color.normal};
      padding-right: 0.5rem;
      font-size: 1.25rem;
    }
  }
  .product-services-button,
  .product-services-back-button {
    display: none;
  }
  .product-services-wrapper .product-services-longdescription .longdescription-headline {
    font-weight: bold;
    margin: 1rem 0;
    cursor: default;
  }
  .product-services-wrapper
    .product-services-longdescription.visible
    .longdescription-headline
    .open,
  .product-services-wrapper
    .product-services-longdescription.visible
    .longdescription-headline
    .close {
    display: none;
  }
`;

const StyledCheck = styled(getIcon('Checkmark'))<{ isFree: boolean }>`
  ${({ theme, isFree }) => isFree && `color: ${theme.success.color.normal}`};
  ${({ theme, isFree }) => isFree && `fill: ${theme.success.color.normal}`};
`;

interface PremiumServiceProps {
  service: Attribute;
}

const PremiumService: FunctionComponent<PremiumServiceProps> = ({ service }): ReactElement => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const isMobile = useMedia({ desktop: false, mobile: true, tablet: false });
  const dispatchGTMEvent = useTracking();
  const {
    articleServices: { serviceIconUrl },
  }: FragmentConfiguration = useConfig();

  return (
    <>
      {service.values.map((value: AttributeValueObject) => (
        <ServiceWrapper mobile={isMobile}>
          <ServiceCheck>
            <StyledCheck size="1.375rem" isFree />
          </ServiceCheck>
          <PremiumServiceIcon>
            <Image
              src={serviceIconUrl?.replace('<serviceicon>', value.iconUrl)}
              alt={value.text}
              fallback={() => null}
            />
          </PremiumServiceIcon>
          <ServiceName>
            {value.text}
            <ModalLink
              data-testid="service-modal-link"
              onClick={() => {
                setIsModalOpen(true);
                dispatchGTMEvent({
                  event: 'ga_event',
                  eventAction: 'viewLayer',
                  eventCategory: 'premiumServiceLayer',
                  eventLabel: 'detailview',
                  eventValue: value.text,
                });
              }}
            >
              <FormattedMessage id="article-service.modal-link" defaultMessage="weitere Infos" />
            </ModalLink>
            <Modal
              isOpen={isModalOpen}
              onRequestClose={() => setIsModalOpen(false)}
              headline={value.text}
            >
              <ModalEsiContainer>
                <Esi src={value.link} />
              </ModalEsiContainer>
            </Modal>
          </ServiceName>
          <ServicePrice inclusive>
            <FormattedMessage id="article-service.inclusive" defaultMessage="inklusive" />
          </ServicePrice>
        </ServiceWrapper>
      ))}
    </>
  );
};

interface ClusterServiceProps {
  service: Service;
}

const ClusterService: FunctionComponent<ClusterServiceProps> = ({ service }): ReactElement => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const dispatchGTMEvent = useTracking();
  const isMobile = useMedia({
    desktop: false,
    mobile: true,
    tablet: false,
  });
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    articleServices: { serviceIconUrl, serviceModalUrl, currency },
  }: FragmentConfiguration = useConfig();
  const { addService, removeService, services } = useBasket();

  useEffect(() => {
    if (
      (service.gratis || service.preselected || service.mandatory) &&
      !services.includes(service.uuid)
    ) {
      addService(service.uuid);
    }
  }, []);

  return (
    <ServiceWrapper data-testid="cluster-service-wrapper" mobile={isMobile}>
      <ServiceCheck>
        {service.gratis && service.preselected && service.mandatory ? (
          <StyledCheck size="1.375rem" isFree />
        ) : (
          <ServiceCheckboxWrapper>
            <ServiceCheckbox
              type="checkbox"
              checked={
                service.mandatory || (service.preselected && services.includes(service.uuid))
              }
              disabled={service.mandatory}
              id={`service-${service.uuid}`}
              onChange={(event) =>
                event.target.checked ? addService(service.uuid) : removeService(service.uuid)
              }
              data-testid="service-checkbox"
            />
            <ServiceCheckboxLabel htmlFor={`service-${service.uuid}`}>
              <StyledCheck size="1rem" isFree={service.gratis} />
            </ServiceCheckboxLabel>
          </ServiceCheckboxWrapper>
        )}
      </ServiceCheck>
      <ServiceIcon htmlFor={`service-${service.uuid}`} inclusive={service.gratis}>
        <Image
          src={serviceIconUrl?.replace('<serviceicon>', service.serviceIcon)}
          alt={service.name}
          fallback={() => null}
        />
      </ServiceIcon>
      <ServiceName>
        {service.gratis && service.mandatory ? (
          service.name
        ) : (
          <label htmlFor={`service-${service.uuid}`}>{service.name}</label>
        )}
        <ModalLink
          data-testid="service-modal-link"
          onClick={() => {
            setIsModalOpen(true);
            dispatchGTMEvent({
              event: 'ga_event',
              eventAction: 'viewLayer',
              eventCategory: 'serviceLayer',
              eventLabel: 'detailview',
              eventValue: service.name,
            });
          }}
        >
          <FormattedMessage id="article-service.modal-link" defaultMessage="weitere Infos" />
        </ModalLink>
        <Modal
          isOpen={isModalOpen}
          onRequestClose={() => setIsModalOpen(false)}
          headline={service.name}
        >
          <div data-testid="service-modal">
            <ModalEsiContainer>
              <Esi
                src={useUrlParameters(
                  serviceModalUrl,
                  {
                    headline: encodeURI(service.name),
                    serviceicon: service.serviceIcon,
                    serviceuuid: service.uuid,
                  },
                  true,
                )}
              />
            </ModalEsiContainer>
          </div>
        </Modal>
      </ServiceName>
      <ServicePrice inclusive={service.gratis}>
        {service.gratis ? (
          <FormattedMessage id="article-service.inclusive" defaultMessage="inklusive" />
        ) : (
          <label htmlFor={`service-${service.uuid}`}>
            <span>+</span>
            {/* disable style must be an object warning for FormattedNumber */}
            {/* eslint-disable-next-line react/style-prop-object */}
            <FormattedCurrency currency={currency} value={service.price * 100} />
          </label>
        )}
      </ServicePrice>
    </ServiceWrapper>
  );
};

interface ClusterProps {
  values: Cluster;
}

const ServiceCluster: FunctionComponent<ClusterProps> = ({ values }): ReactElement => {
  const isMobile = useMedia({
    desktop: false,
    mobile: true,
    tablet: false,
  });

  return (
    <ClusterWrapper mobile={isMobile}>
      <ClusterLabel mobile={isMobile}>{values.label}</ClusterLabel>
      {values.services.map((service) => (
        <ClusterService service={service} key={service.uuid} />
      ))}
    </ClusterWrapper>
  );
};

export interface ArticleServicesProps {
  /* The article order number. */
  orderNumber: string;
  /* The article price. */
  price: number;
  /** The premium services. */
  premiumServices: AttributeBlock;
}

/**
 * This components renders additional services for the given product.
 */
export const ArticleServices: FunctionComponent<ArticleServicesProps> = ({
  orderNumber,
  price,
  premiumServices,
}): ReactElement => {
  const isMobile = useMedia({
    desktop: false,
    mobile: true,
    tablet: false,
  });
  const {
    articleServices: { articleServicesEndpoint },
  }: FragmentConfiguration = useConfig();
  const fetchConfig = {
    method: 'GET',
  };

  const url = useUrlParameters(articleServicesEndpoint, {
    ordernumber: orderNumber,
    price: price.toString(),
  });

  const { error, isLoading, response } = useFetch<Services>({
    fetchConfig,
    url,
  });

  const servicesHeadline = (
    <ServicesHeadline mobile={isMobile}>
      <TippBadge />
      <span>
        <FormattedMessage
          id="article-services.headline"
          defaultMessage="Services jetzt dazubestellen"
        />
      </span>
    </ServicesHeadline>
  );

  return (
    <>
      {isLoading && <></>}
      {error && <></>}
      {response &&
        response.clusters.length > 0 &&
        (isMobile ? (
          <Card headline={servicesHeadline} contentPadding>
            <ServicesWrapper mobile={isMobile} data-testid="article-services-mobile">
              {response.clusters.map((cluster) => (
                <ServiceCluster key={cluster.label} values={cluster} />
              ))}
              {premiumServices && (
                <ClusterWrapper mobile={isMobile}>
                  {premiumServices.values.map((service, index) => (
                    <PremiumService service={service} key={service.type.concat(index.toFixed())} />
                  ))}
                </ClusterWrapper>
              )}
            </ServicesWrapper>
          </Card>
        ) : (
          <ServicesWrapper mobile={isMobile} data-testid="article-services">
            {servicesHeadline}
            {response.clusters.map((cluster) => (
              <ServiceCluster key={cluster.label} values={cluster} />
            ))}
            {premiumServices && (
              <ClusterWrapper mobile={isMobile}>
                {premiumServices.values.map((service, index) => (
                  <PremiumService service={service} key={service.type.concat(index.toFixed())} />
                ))}
              </ClusterWrapper>
            )}
          </ServicesWrapper>
        ))}
    </>
  );
};

export default ArticleServices;
